import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
} from 'react';
import { UserMeResponse, ProductItem } from '@/lib/types';
import { apiGet } from '@/services/api';
import { getProductsList } from '@/services/product.service';
import { useToast } from '@/hooks/use-toast';
import { Loader2 } from 'lucide-react';

interface UserContextType {
  userData: UserMeResponse | null;
  productsData: ProductItem[] | null;
  isLoading: boolean;
  totalItems: number;
  totalPages: number;
  currentPage: number;
  searchQuery: string;
  categoryQuery: string | null;
  sourceQuery: string | null;
  setPage: (page: number) => void;
  setSearch: (query: string) => void;
  refetchUserData: () => Promise<void>;
  setCategory: (category: string | null) => void;
  setSource: (source: string | null) => void;
}

const UserContext = createContext<UserContextType | undefined>(undefined);

export const UserProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [userData, setUserData] = useState<UserMeResponse | null>(null);
  const [productsData, setProductsData] = useState<ProductItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState('');
  const [categoryQuery, setCategoryQuery] = useState<string | null>(null);
  const [sourceQuery, setSourceQuery] = useState<string | null>(null);
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const { toast } = useToast();

  const fetchUserData = useCallback(async () => {
    try {
      setIsLoading(true);
      const [userDataResponse, productsDataResponse] = await Promise.all([
        apiGet('/users/me'),
        getProductsList(
          currentPage,
          20,
          searchQuery,
          categoryQuery,
          sourceQuery
        ),
      ]);
      setUserData(userDataResponse as UserMeResponse);
      setProductsData(productsDataResponse.items);
      setTotalItems(productsDataResponse.total);
      setTotalPages(productsDataResponse.pages_count);
    } catch (error) {
      console.error('Failed to fetch user data:', error);
      toast({
        title: 'Error',
        description: 'Failed to load user data',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
      setIsInitialLoad(false);
    }
  }, [currentPage, searchQuery, categoryQuery, sourceQuery, toast]);

  useEffect(() => {
    fetchUserData();
  }, [fetchUserData]);

  return (
    <UserContext.Provider
      value={{
        userData,
        productsData,
        isLoading,
        totalItems,
        totalPages,
        currentPage,
        searchQuery,
        categoryQuery,
        sourceQuery,
        setPage: setCurrentPage,
        setSearch: setSearchQuery,
        refetchUserData: fetchUserData,
        setCategory: setCategoryQuery,
        setSource: setSourceQuery,
      }}
    >
      {isInitialLoad ? (
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="flex flex-col items-center gap-3">
            <Loader2 className="w-8 h-8 animate-spin text-primary" />
            <p className="text-sm text-muted-foreground">
              Loading user data...
            </p>
          </div>
        </div>
      ) : (
        children
      )}
    </UserContext.Provider>
  );
};

export const useUser = () => {
  const context = useContext(UserContext);
  if (context === undefined) {
    throw new Error('useUser must be used within a UserProvider');
  }
  return context;
};
